import { useEffect, useMemo, useState } from "react";
import { api, errDetail } from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { AiAgentDetailDialog } from "../components/AiAgentDetailDialog";
import { useApp } from "../lib/store";
import { Bot, Sparkles, Search, Plus, RotateCcw } from "lucide-react";

const CAT_BADGE = {
  crm: "bg-blue-100 text-blue-700",
  srm: "bg-amber-100 text-amber-800",
  allgemein: "bg-slate-100 text-slate-700",
};
const CAT_LABEL = { crm: "CRM", srm: "SRM", allgemein: "Allgemein" };

const fmtTokens = (n) => { n = n || 0; return n >= 1000 ? (n / 1000).toFixed(1).replace(".", ",") + "k" : String(n); };
const fmtCost = (c) => "$" + (c || 0).toFixed(c && c < 0.01 ? 4 : 2);

function Sparkline({ data }) {
  const max = Math.max(...(data || [0]), 0);
  if (!data || !data.length || max === 0) {
    return <span className="text-[10px] text-gray-300">Noch keine Nutzung</span>;
  }
  const w = 110, h = 22, step = w / (data.length - 1);
  const pts = data.map((v, i) => `${(i * step).toFixed(1)},${(h - (v / max) * (h - 3) - 1).toFixed(1)}`).join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="shrink-0" aria-hidden>
      <polyline points={pts} fill="none" stroke="#F5A623" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function AgentCard({ a, onOpen }) {
  return (
    <button onClick={() => onOpen(a.id)} data-testid={`ai-agent-card-${a.id}`}
      className="text-left bg-white rounded-xl border border-gray-200 p-4 hover:border-[#F5A623] hover:shadow-md transition-all duration-150 flex flex-col">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="h-10 w-10 rounded-lg bg-[#1B2A4A] flex items-center justify-center shrink-0">
          <Bot className="h-5 w-5 text-[#F5A623]" strokeWidth={2} />
        </div>
        <div className="flex items-center gap-1.5">
          {a.archived && <Badge className="bg-gray-800 text-white hover:bg-gray-800">Archiviert</Badge>}
          <Badge className={a.status === "active" ? "bg-green-100 text-green-700 hover:bg-green-100" : "bg-gray-200 text-gray-600 hover:bg-gray-200"}>
            {a.status === "active" ? "Aktiv" : "Inaktiv"}
          </Badge>
        </div>
      </div>
      <div className="font-semibold text-[#1B2A4A] leading-tight">{a.name}</div>
      <p className="text-sm text-gray-500 mt-1 line-clamp-3 flex-1">{a.short_description}</p>
      <div className="flex flex-wrap items-center gap-1.5 mt-3">
        <Badge className={`${CAT_BADGE[a.category] || CAT_BADGE.allgemein} hover:opacity-100`}>{CAT_LABEL[a.category] || a.category}</Badge>
        <Badge variant="outline" className="gap-1 border-gray-300 text-gray-600 font-normal"><Sparkles className="h-3 w-3" />{a.model_badge}</Badge>
      </div>
      <div className="mt-3 pt-3 border-t border-gray-100" data-testid={`ai-agent-trend-${a.id}`}>
        <div className="flex items-center justify-between gap-2">
          <div className="text-[11px] text-gray-400 leading-tight">
            <span className="font-semibold text-[#1B2A4A] text-sm">{a.usage?.calls_30d ?? 0}</span> Aufrufe<br />letzte 30 Tage
          </div>
          <Sparkline data={a.usage?.trend} />
        </div>
        <div className="flex items-center justify-between gap-2 mt-2 text-[11px] text-gray-500">
          <span data-testid={`ai-agent-tokens-${a.id}`}>{fmtTokens(a.usage?.tokens_total)} Tokens</span>
          <span data-testid={`ai-agent-cost-${a.id}`}>≈ {fmtCost(a.usage?.cost_usd)}</span>
        </div>
      </div>
    </button>
  );
}

function CreateAgentDialog({ open, onOpenChange, models, onCreated }) {
  const empty = { name: "", category: "allgemein", short_description: "", purpose: "", system_prompt: "", behavior_notes: "", example_input: "", example_output: "", model_key: models[0] ? `${models[0].provider}|${models[0].model}` : "" };
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF({ ...empty, model_key: models[0] ? `${models[0].provider}|${models[0].model}` : "" }); }, [open]); // eslint-disable-line
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!f.name.trim() || !f.short_description.trim() || !f.system_prompt.trim() || !f.model_key) {
      toast.error("Bitte alle Pflichtfelder ausfüllen (Name, Kategorie, Kurzbeschreibung, System-Prompt, Modell)");
      return;
    }
    setSaving(true);
    try {
      const [model_provider, model_name] = f.model_key.split("|");
      const r = await api.post("/ai-agents", {
        name: f.name, category: f.category, short_description: f.short_description, purpose: f.purpose,
        system_prompt: f.system_prompt, behavior_notes: f.behavior_notes,
        example_input: f.example_input, example_output: f.example_output,
        model_provider, model_name, status: "active",
      });
      toast.success("Agent angelegt");
      onOpenChange(false); onCreated?.(r.data);
    } catch (e) { toast.error(errDetail(e)); }
    setSaving(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="ai-agent-create-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#1B2A4A]">Neuen KI-Agenten anlegen</DialogTitle>
          <DialogDescription>Pflichtfelder: Name, Kategorie, Kurzbeschreibung, System-Prompt, Modell.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Name *</label>
            <Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="ai-agent-new-name" className="mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Kategorie *</label>
              <Select value={f.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="mt-1" data-testid="ai-agent-new-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="crm">CRM</SelectItem>
                  <SelectItem value="srm">SRM</SelectItem>
                  <SelectItem value="allgemein">Allgemein</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Modell *</label>
              <Select value={f.model_key} onValueChange={(v) => set("model_key", v)}>
                <SelectTrigger className="mt-1" data-testid="ai-agent-new-model"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {models.map((m) => <SelectItem key={`${m.provider}|${m.model}`} value={`${m.provider}|${m.model}`}>{m.label} · {m.provider}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Kurzbeschreibung *</label>
            <Textarea value={f.short_description} onChange={(e) => set("short_description", e.target.value)} rows={2} data-testid="ai-agent-new-desc" className="mt-1 text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Zweck & Einsatzkontext</label>
            <Textarea value={f.purpose} onChange={(e) => set("purpose", e.target.value)} rows={2} className="mt-1 text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">System-Prompt *</label>
            <Textarea value={f.system_prompt} onChange={(e) => set("system_prompt", e.target.value)} rows={5} data-testid="ai-agent-new-prompt" className="mt-1 font-mono text-xs" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Verhaltensbeschreibung</label>
            <Textarea value={f.behavior_notes} onChange={(e) => set("behavior_notes", e.target.value)} rows={2} className="mt-1 text-sm" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={submit} disabled={saving} data-testid="ai-agent-new-submit" className="bg-[#1B2A4A] hover:bg-[#1B2A4A]/90">{saving ? "Speichert…" : "Agent anlegen"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AiAgents() {
  const { perms, user } = useApp();
  const isAdmin = !!(perms?.is_admin || user?.role === "admin");
  const [agents, setAgents] = useState(null);
  const [models, setModels] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [status, setStatus] = useState("all");
  const [openId, setOpenId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = () => api.get("/ai-agents?include_archived=true").then((r) => setAgents(r.data || [])).catch((e) => toast.error(errDetail(e)));
  useEffect(() => {
    load();
    api.get("/ai-agents/models").then((r) => setModels(r.data || [])).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (!agents) return [];
    const ql = q.trim().toLowerCase();
    return agents.filter((a) => {
      if (status === "archived") { if (!a.archived) return false; }
      else { if (a.archived) return false; if (status !== "all" && a.status !== status) return false; }
      if (cat !== "all" && a.category !== cat) return false;
      if (ql && !(`${a.name} ${a.short_description}`.toLowerCase().includes(ql))) return false;
      return true;
    });
  }, [agents, q, cat, status]);

  const reset = () => { setQ(""); setCat("all"); setStatus("all"); };
  const hasFilters = q || cat !== "all" || status !== "all";

  return (
    <div className="max-w-[1300px] mx-auto" data-testid="ai-agents-page">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-[#1B2A4A] flex items-center justify-center"><Bot className="h-5 w-5 text-[#F5A623]" /></div>
          <div>
            <h1 className="text-2xl font-bold text-[#1B2A4A]">KI-Menü</h1>
            <p className="text-sm text-gray-500">Übersicht aller KI-Agenten in Encasa CRM &amp; SRM</p>
          </div>
        </div>
        {isAdmin && (
          <Button onClick={() => setCreateOpen(true)} data-testid="ai-agent-add-btn" className="bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A] font-semibold rounded-full px-4 gap-1">
            <Plus className="h-4 w-4" strokeWidth={2.5} />Agent hinzufügen
          </Button>
        )}
      </div>

      {/* Filter */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Agent suchen…" className="pl-9 h-9 bg-white" data-testid="ai-agent-search" />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="w-40 h-9 bg-white" data-testid="ai-agent-filter-category"><SelectValue placeholder="Kategorie" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Kategorien</SelectItem>
            <SelectItem value="crm">CRM</SelectItem>
            <SelectItem value="srm">SRM</SelectItem>
            <SelectItem value="allgemein">Allgemein</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-36 h-9 bg-white" data-testid="ai-agent-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Status</SelectItem>
            <SelectItem value="active">Aktiv</SelectItem>
            <SelectItem value="inactive">Inaktiv</SelectItem>
            <SelectItem value="archived">Archiviert</SelectItem>
          </SelectContent>
        </Select>
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={reset} data-testid="ai-agent-reset-filters" className="h-9 gap-1 text-gray-500">
            <RotateCcw className="h-3.5 w-3.5" />Zurücksetzen
          </Button>
        )}
      </div>

      {/* Grid / states */}
      {agents === null ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => <div key={i} className="h-40 rounded-xl bg-gray-100 animate-pulse" />)}
        </div>
      ) : agents.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl border border-dashed border-gray-300" data-testid="ai-agents-empty">
          <Bot className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 mb-4">Noch keine KI-Anbindung konfiguriert</p>
          {isAdmin && (
            <Button onClick={() => setCreateOpen(true)} data-testid="ai-agent-add-btn-empty" className="bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A] font-semibold gap-1">
              <Plus className="h-4 w-4" strokeWidth={2.5} />Agent hinzufügen
            </Button>
          )}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400" data-testid="ai-agents-no-match">Kein Agent entspricht den Filtern.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ai-agents-grid">
          {filtered.map((a) => <AgentCard key={a.id} a={a} onOpen={setOpenId} />)}
        </div>
      )}

      <AiAgentDetailDialog agentId={openId} open={!!openId} onOpenChange={(o) => !o && setOpenId(null)} onChanged={load} />
      <CreateAgentDialog open={createOpen} onOpenChange={setCreateOpen} models={models} onCreated={(a) => { load(); setOpenId(a.id); }} />
    </div>
  );
}
